// FrequentationEntriesTable component
// CREATED: Split from FrequentationEntries.tsx to keep file sizes manageable
// Contains: DesktopTable, MobileList, SortIcon, ValueBadge sub-components,
//   formatDate and formatTime utilities
// UPDATED: Added Horaire column to DesktopTable; time display in MobileList
// FIXED: sortEntries now uses ?? null (not ?? '') for scheduledTime/missionName/userName
//   so null values are correctly sorted last via the null-guard branches

import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { FrequentationEntry } from '../../lib/analytics';

export type SortField = 'occurrenceDate' | 'scheduledTime' | 'missionName' | 'userName' | 'numericValue';
export type SortDirection = 'asc' | 'desc';

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function formatTime(time: string | null): string {
  if (!time) return '—';
  return time.substring(0, 5).replace(':', 'h');
}

export function sortEntries(
  entries: FrequentationEntry[],
  field: SortField | null,
  direction: SortDirection
): FrequentationEntry[] {
  if (!field) return entries;
  return [...entries].sort((a, b) => {
    let aVal: string | number | null;
    let bVal: string | number | null;
    if (field === 'occurrenceDate') {
      aVal = a.occurrenceDate;
      bVal = b.occurrenceDate;
    } else if (field === 'scheduledTime') {
      aVal = a.scheduledTime ?? null;
      bVal = b.scheduledTime ?? null;
    } else if (field === 'missionName') {
      aVal = a.missionName ?? null;
      bVal = b.missionName ?? null;
    } else if (field === 'userName') {
      aVal = a.userName ?? null;
      bVal = b.userName ?? null;
    } else {
      aVal = a.numericValue;
      bVal = b.numericValue;
    }
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return direction === 'asc' ? aVal.localeCompare(bVal, 'fr') : bVal.localeCompare(aVal, 'fr');
    }
    const diff = (aVal as number) - (bVal as number);
    return direction === 'asc' ? diff : -diff;
  });
}

interface SortIconProps {
  field: SortField;
  active: SortField | null;
  direction: SortDirection;
}

function SortIcon({ field, active, direction }: SortIconProps) {
  if (active !== field) {
    return <ArrowUpDown size={12} className="text-gray-300 ml-1 flex-shrink-0" />;
  }
  if (direction === 'asc') {
    return <ArrowUp size={12} className="text-gray-600 ml-1 flex-shrink-0" />;
  }
  return <ArrowDown size={12} className="text-gray-600 ml-1 flex-shrink-0" />;
}

function ValueBadge({ value }: { value: number | null }) {
  if (value != null) {
    return (
      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 bg-primary-50 text-primary-700 font-semibold text-sm rounded-md tabular-nums">
        {value}
      </span>
    );
  }
  return <span className="text-xs text-gray-400 italic">Non renseigné</span>;
}

export interface DesktopTableProps {
  entries: FrequentationEntry[];
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

export function DesktopTable({ entries, sortField, sortDirection, onSort }: DesktopTableProps) {
  const thBase = 'px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide';
  const thButton = 'flex items-center gap-0 hover:text-gray-700 transition-colors cursor-pointer select-none';

  return (
    <div className="hidden sm:block overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            <th className={`${thBase} w-36`}>
              <button type="button" className={thButton} onClick={() => onSort('occurrenceDate')}>
                Date
                <SortIcon field="occurrenceDate" active={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className={`${thBase} w-24`}>
              <button type="button" className={thButton} onClick={() => onSort('scheduledTime')}>
                Horaire
                <SortIcon field="scheduledTime" active={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className={thBase}>
              <button type="button" className={thButton} onClick={() => onSort('missionName')}>
                Mission
                <SortIcon field="missionName" active={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className={thBase}>
              <button type="button" className={thButton} onClick={() => onSort('userName')}>
                Utilisateur assigné
                <SortIcon field="userName" active={sortField} direction={sortDirection} />
              </button>
            </th>
            <th className={`${thBase} text-right w-36`}>
              <button
                type="button"
                className={`${thButton} ml-auto`}
                onClick={() => onSort('numericValue')}
              >
                Fréquentation
                <SortIcon field="numericValue" active={sortField} direction={sortDirection} />
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {entries.map(entry => (
            <tr key={entry.occurrenceId} className="hover:bg-gray-50/60 transition-colors">
              <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap capitalize">
                {formatDate(entry.occurrenceDate)}
              </td>
              <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap tabular-nums font-medium">
                {formatTime(entry.scheduledTime)}
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-900 font-medium">
                {entry.missionName}
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-600">
                {entry.userName ?? (
                  <span className="text-gray-400 italic text-xs">Inconnu</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right">
                <ValueBadge value={entry.numericValue} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MobileList({ entries }: { entries: FrequentationEntry[] }) {
  return (
    <div className="sm:hidden divide-y divide-gray-100">
      {entries.map(entry => (
        <div
          key={entry.occurrenceId}
          className="px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate">{entry.missionName}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="capitalize">{formatDate(entry.occurrenceDate)}</span>
              {entry.scheduledTime && (
                <>
                  <span className="mx-1 text-gray-300">·</span>
                  <span className="tabular-nums">{formatTime(entry.scheduledTime)}</span>
                </>
              )}
              {entry.userName && (
                <>
                  <span className="mx-1 text-gray-300">·</span>
                  <span>{entry.userName}</span>
                </>
              )}
            </p>
          </div>
          <div className="flex-shrink-0">
            {entry.numericValue != null ? (
              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 bg-primary-50 text-primary-700 font-semibold text-sm rounded-md tabular-nums">
                {entry.numericValue}
              </span>
            ) : (
              <span className="text-xs text-gray-400 italic">—</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
